import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PRESETS } from '@/data/presets';
import { useStudio } from '@/store/StudioStore';
import { CREATION_TYPES, TOPIC_CATEGORIES, FONT_FAMILIES } from '@/data/optionsCore';
import { BACKGROUND_MODES } from '@/data/optionsLibrary';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'everyday', label: 'Everyday' },
  { id: 'halloween', label: 'Halloween' },
  { id: 'holiday', label: 'Other Holidays' },
  { id: 'complete-design', label: 'Complete Designs' },
];

export default function Presets() {
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');
  const { dispatch } = useStudio();
  const nav = useNavigate();

  const filtered = useMemo(() => PRESETS.filter((p) => (cat === 'all' || p.category === cat) && (!q || p.name.toLowerCase().includes(q.toLowerCase()))), [cat, q]);

  const apply = (preset) => {
    const patch = { ...preset.recipe };
    if (patch.creationType) { const c = CREATION_TYPES.find((x) => x.id === patch.creationType); if (c) patch.creationTypeLabel = c.label; }
    if (patch.topicCategory) { const t = TOPIC_CATEGORIES.find((x) => x.id === patch.topicCategory); if (t) patch.topicCategoryLabel = t.label; }
    if (patch.fontFamily) { const f = FONT_FAMILIES.find((x) => x.id === patch.fontFamily); if (f) patch.fontFamilyLabel = f.label; }
    if (patch.background) { const b = BACKGROUND_MODES.find((x) => x.id === patch.background); if (b) patch.backgroundLabel = b.label; }
    dispatch({ type: 'PATCH', patch });
    toast.success(`Preset applied: ${preset.name}`);
    nav('/studio');
  };

  return (
    <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10" data-testid="presets-page">
      <div className="mb-6">
        <div className="eyebrow">Curated Recipes</div>
        <h1 className="font-display text-5xl">Presets</h1>
        <p className="text-zinc-400 mt-2">Start from a complete editable recipe. Every preset opens in the studio ready to customize.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 items-center">
        {CATEGORIES.map((c) => (
          <button key={c.id} data-testid={`preset-cat-${c.id}`} onClick={() => setCat(c.id)} className={`chip ${cat === c.id ? 'active' : ''}`}>{c.label}</button>
        ))}
        <input data-testid="preset-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search presets…" className="ml-auto bg-zinc-950 border border-zinc-800 rounded-full px-4 py-2 text-sm" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((p, i) => (
          <div key={p.id} className="rounded-2xl chrome-border chrome-hover bg-zinc-900/60 p-5 fade-up" style={{ animationDelay: `${i * 30}ms` }} data-testid={`preset-card-${p.id}`}>
            <div className="flex items-center justify-between">
              <div className="eyebrow">{p.badge || p.category}</div>
              {p.holiday && <span className="text-[10px] uppercase tracking-widest text-pink-500">{p.holiday}</span>}
            </div>
            <h3 className="font-display text-2xl mt-2">{p.name}</h3>
            <div className="mt-3 text-xs text-zinc-400 space-y-0.5">
              {Object.entries(p.recipe).slice(0, 5).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2">
                  <span className="text-zinc-600">{k}</span>
                  <span className="text-zinc-300 text-right truncate max-w-[180px]">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                </div>
              ))}
            </div>
            <button data-testid={`apply-preset-${p.id}`} onClick={() => apply(p)} className="btn-magenta w-full mt-4 rounded-full py-2 text-xs uppercase tracking-widest">Load Preset</button>
          </div>
        ))}
      </div>
    </main>
  );
}
